import { createApp } from 'vue'
import * as Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
// Element Plus 默认是英文文案（分页的 Total/page、表格的 No Data、日期选择器等），
// 这里统一挂上中文语言包，避免各页面出现中英混排。
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
// 暗色主题变量：右上角主题切换按钮依赖它，不引入则 html.dark 加上了也没有样式响应
import 'element-plus/theme-chalk/dark/css-vars.css'
import './styles/mobile-responsive.css'  // 全局移动端响应式样式
import './styles/workflow-theme.css'     // 工作流模块统一运维风格主题
import * as ECharts from 'echarts'
import VueECharts from 'vue-echarts'
import permissionDirective from './directives/permission'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import axios from 'axios'
import { getToken } from './utils/auth'
import { applyTheme, getSavedTheme } from './utils/theme'

// 挂载之前就把上次选择的主题应用上，避免刷新时先闪一下浅色
applyTheme(getSavedTheme())

// 项目里有 6 个页面直接用裸 axios 请求 /api/...（不走 utils/request.js 与 services/api.js），
// 这些请求不会自动带登录令牌，后端启用鉴权后会被拒。
// 这里给全局 axios 默认实例补一个拦截器，一次性覆盖这些调用点；
// axios.create() 出来的实例有独立拦截器，不受影响。
axios.interceptors.request.use(config => {
  const token = getToken()
  if (token) {
    config.headers = config.headers || {}
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

// 项目里还有几处直接用原生 fetch 请求 /api/...（不走 axios），同样不会带令牌，
// 后端鉴权一旦启用就会被判为匿名访问。这里包装全局 fetch 统一补上，
// 只对"同源且以 /api/ 开头"的请求注入，避免令牌被发送到外部地址
// （例如系统设置里的连通性测试 URL 是用户自己填的）。
const nativeFetch = window.fetch.bind(window)
window.fetch = (input, init) => {
  try {
    const rawUrl = typeof input === 'string' ? input : (input && input.url)
    const token = getToken()
    if (token && rawUrl) {
      const resolved = new URL(rawUrl, window.location.origin)
      if (resolved.origin === window.location.origin && resolved.pathname.startsWith('/api/')) {
        const options = Object.assign({}, init)
        const headers = new Headers(options.headers || (typeof input === 'string' ? undefined : input.headers))
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`)
          options.headers = headers
          return nativeFetch(input, options)
        }
      }
    }
  } catch (e) {
    // URL 解析失败时按原样放行，不影响正常请求
  }
  return nativeFetch(input, init)
}

window.Vue = Vue

// 忽略 ResizeObserver 循环警告，避免 webpack-dev-server overlay 弹窗
const ignoreResizeObserverError = (e) => {
  if (e && e.message && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation && e.stopImmediatePropagation()
  }
}

window.addEventListener('error', ignoreResizeObserverError)
window.addEventListener('unhandledrejection', ignoreResizeObserverError)

const app = createApp(App)

// 全局注册所有 Element Plus 图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(router)
app.use(store)
app.use(ElementPlus, { locale: zhCn })
app.use(permissionDirective)
app.component('v-chart', VueECharts)

app.config.globalProperties.$echarts = ECharts

app.mount('#app')